import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "buttons-wrapper d-flex mb-4 flex-md-row flex-column align-center" }
const _hoisted_2 = { class: "d-flex align-center no-wrap" }
const _hoisted_3 = { class: "damage-table-wrapper" }
const _hoisted_4 = { class: "square-cell" }
const _hoisted_5 = ["data-attack", "data-armor"]
const _hoisted_6 = {
  key: 0,
  class: "timer-display mt-4"
}
const _hoisted_7 = {
  key: 0,
  class: "confetti-center"
}

import { nextTick, ref, watch } from 'vue';

// --- Armor Images ---
import HeavyArmorImage from '@/assets/imgs/damageValueGame/armors/heavy-armor.png';
import MediumArmorImage from '@/assets/imgs/damageValueGame/armors/medium-armor.png';
import LightArmorImage from '@/assets/imgs/damageValueGame/armors/light-armor.png';
import UnarmoredImage from '@/assets/imgs/damageValueGame/armors/unarmored.png';
import HeroArmorImage from '@/assets/imgs/damageValueGame/armors/hero-armor.png';
import FortifiedArmorImage from '@/assets/imgs/damageValueGame/armors/fortified-armor.png';

// --- Attack Images ---
import normalAttackImage from '@/assets/imgs/damageValueGame/damage/normal-attack.png';
import piercingAttackImage from '@/assets/imgs/damageValueGame/damage/piercing-attack.png';
import magicAttackImage from '@/assets/imgs/damageValueGame/damage/magic-attack.png';
import chaosAttackImage from '@/assets/imgs/damageValueGame/damage/chaos-attack.png';
import heroAttackImage from '@/assets/imgs/damageValueGame/damage/hero-attack.png';
import siegeAttackImage from '@/assets/imgs/damageValueGame/damage/siege-attack.png';
import spellAttackImage from '@/assets/imgs/damageValueGame/damage/spell-attack.png';
import { Armor, ArmorType, Attack, AttackType, DamageGame } from '@/pages/Games/AttackArmorValueGame/AttackArmorValueGame.model';

import ConfettiExplosion from 'vue-confetti-explosion';

export default /*@__PURE__*/_defineComponent({
  __name: 'AttackArmorValueGame',
  setup(__props) {

const game = new DamageGame();

const armors = ref([
  new Armor('Heavy', HeavyArmorImage),
  new Armor('Medium', MediumArmorImage),
  new Armor('Light', LightArmorImage),
  new Armor('Unarmored', UnarmoredImage),
  new Armor('Fortified', FortifiedArmorImage),
  new Armor('Hero', HeroArmorImage),
]);

const attacks = ref([
  new Attack('Normal', normalAttackImage),
  new Attack('Piercing', piercingAttackImage),
  new Attack('Magic', magicAttackImage),
  new Attack('Siege', siegeAttackImage),
  new Attack('Hero', heroAttackImage),
  new Attack('Chaos', chaosAttackImage),
  new Attack('Spell', spellAttackImage),
]);

const userInputs = ref<Record<string, Record<string, string>>>({});
const results = ref<Record<string, Record<string, boolean | null>>>({});
const focusedRow = ref<string | null>(null);
const showConfetti = ref(false);

const hardcoreMode = ref(false);
const timer = ref(0);
const timerDisplay = ref('00:00.00');
const timerInterval = ref<number | null>(null);
const timerRunning = ref(false);

watch(
  results,
  (newResults) => {
    const allFilled = Object.values(newResults).every((attackRow) => Object.values(attackRow).every((val) => val === true));

    if (allFilled) {
      stopTimer();
      explode();
    }
  },
  { deep: true }
);

watch(hardcoreMode, (enabled) => {
  if (enabled) {
    shuffleTable();
    resetTable();
    resetTimer();
  } else {
    stopTimer();
    resetTimer();
  }
});

initializeTables();

function initializeTables(): void {
  for (const atk of attacks.value) {
    userInputs.value[atk.name] = {};
    results.value[atk.name] = {};
    for (const arm of armors.value) {
      userInputs.value[atk.name][arm.name] = '';
      results.value[atk.name][arm.name] = null;
    }
  }
}

function shuffle<T>(array: T[]): T[] {
  return array.sort(() => Math.random() - 0.5);
}

function shuffleTable(): void {
  armors.value = shuffle([...armors.value]);
  attacks.value = shuffle([...attacks.value]);
}

function startTimer(): void {
  if (timerRunning.value) return;
  timerRunning.value = true;
  const startTime = performance.now() - timer.value;
  timerInterval.value = window.setInterval(() => {
    timer.value = performance.now() - startTime;
    timerDisplay.value = formatTime(timer.value);
  }, 10);
}

function stopTimer(): void {
  if (timerInterval.value) {
    clearInterval(timerInterval.value);
    timerInterval.value = null;
  }
  timerRunning.value = false;
}

function resetTimer(): void {
  stopTimer();
  timer.value = 0;
  timerDisplay.value = '00:00.00';
}

function formatTime(ms: number): string {
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const centiseconds = Math.floor((totalSeconds * 100) % 100);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(centiseconds).padStart(2, '0')}`;
}

function checkValue(attack: AttackType, armor: ArmorType): void {
  const correctValue = game.getDamageValue(attack, armor);
  const userValue = parseFloat(userInputs.value[attack][armor].replace('%', ''));
  if (hardcoreMode.value && !timerRunning.value && !isNaN(userValue)) {
    startTimer();
  }
  if (isNaN(userValue)) {
    results.value[attack][armor] = null;
    return;
  }

  const isCorrect = userValue === correctValue;
  results.value[attack][armor] = isCorrect;

  if (hardcoreMode.value && !isCorrect) {
    resetTable();
    resetTimer();
    shuffleTable();
  }
}

function getCellColor(attack: AttackType, armor: ArmorType): string {
  const result = results.value[attack][armor];
  if (result === null) return 'white';
  return result ? '#c8e6c9' : '#ffcdd2';
}

function handleFocus(attack: AttackType): void {
  focusedRow.value = attack;
}

function handleBlur(): void {
  focusedRow.value = null;
}

function handleKeyNavigation(event: KeyboardEvent, attack: AttackType, armor: ArmorType): void {
  const attackIndex = attacks.value.findIndex((a) => a.name === attack);
  const armorIndex = armors.value.findIndex((a) => a.name === armor);

  let nextAttackIndex = attackIndex;
  let nextArmorIndex = armorIndex;

  switch (event.key) {
    case 'ArrowUp':
      nextAttackIndex = Math.max(0, attackIndex - 1);
      break;
    case 'ArrowDown':
      nextAttackIndex = Math.min(attacks.value.length - 1, attackIndex + 1);
      break;
    case 'ArrowLeft':
      nextArmorIndex = Math.max(0, armorIndex - 1);
      break;
    case 'ArrowRight':
      nextArmorIndex = Math.min(armors.value.length - 1, armorIndex + 1);
      break;
    case 'Enter':
      checkValue(attack, armor);
      (event.target as HTMLInputElement).blur();
      return;
  }

  if (nextAttackIndex !== attackIndex || nextArmorIndex !== armorIndex) {
    event.preventDefault();

    requestAnimationFrame((): void => {
      const wrapper = document.querySelector<HTMLElement>(
        `.cell-wrapper[data-attack="${attacks.value[nextAttackIndex].name}"][data-armor="${armors.value[nextArmorIndex].name}"]`
      );

      const nextInput = wrapper?.querySelector('input');
      nextInput?.focus();
    });
  }
}

function resetTable(): void {
  for (const atk of attacks.value) {
    for (const arm of armors.value) {
      userInputs.value[atk.name][arm.name] = '';
      results.value[atk.name][arm.name] = null;
    }
  }
  resetTimer();
}

async function explode(): Promise<void> {
  showConfetti.value = false;
  await nextTick();
  showConfetti.value = true;
}

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_img = _resolveComponent("v-img")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_table = _resolveComponent("v-table")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_v_container, { class: "py-6 d-flex flex-column align-center" }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(_component_v_btn, {
            color: "primary",
            onClick: shuffleTable
          }, {
            default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
              _createTextVNode("Shuffle Rows & Columns", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_v_btn, {
            color: "error",
            onClick: resetTable
          }, {
            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
              _createTextVNode("Reset All Fields", -1)
            ]))]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_2, [
            _createVNode(_component_v_checkbox, {
              modelValue: hardcoreMode.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((hardcoreMode).value = $event)),
              color: "red",
              "hide-details": "",
              density: "compact"
            }, null, 8, ["modelValue"]),
            _cache[3] || (_cache[3] = _createElementVNode("span", { class: "ml-2 hardcore-label" }, "Hardcore Mode", -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_v_table, { class: "damage-table" }, {
            default: _withCtx(() => [
              _createElementVNode("thead", null, [
                _createElementVNode("tr", null, [
                  _cache[4] || (_cache[4] = _createElementVNode("th", { class: "square-cell" }, null, -1)),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(armors.value, (armor) => {
                    return (_openBlock(), _createElementBlock("th", {
                      key: armor.name,
                      class: "square-cell"
                    }, [
                      _createVNode(_component_v_img, {
                        src: armor.image,
                        class: "fill-cell",
                        alt: armor.name
                      }, null, 8, ["src", "alt"])
                    ]))
                  }), 128))
                ])
              ]),
              _createElementVNode("tbody", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(attacks.value, (attack) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: attack.name,
                    class: _normalizeClass({ 'highlighted-row': focusedRow.value === attack.name })
                  }, [
                    _createElementVNode("td", _hoisted_4, [
                      _createVNode(_component_v_img, {
                        src: attack.image,
                        class: "fill-cell",
                        alt: attack.name
                      }, null, 8, ["src", "alt"])
                    ]),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(armors.value, (armor) => {
                      return (_openBlock(), _createElementBlock("td", {
                        key: armor.name,
                        class: "square-cell"
                      }, [
                        _createElementVNode("div", {
                          class: "cell-wrapper",
                          "data-attack": attack.name,
                          "data-armor": armor.name
                        }, [
                          _createVNode(_component_v_text_field, {
                            modelValue: userInputs.value[attack.name][armor.name],
                            "onUpdate:modelValue": ($event: any) => ((userInputs.value[attack.name][armor.name]) = $event),
                            "hide-details": "",
                            variant: "outlined",
                            density: "compact",
                            class: "fill-cell no-padding",
                            style: _normalizeStyle({ backgroundColor: getCellColor(attack.name, armor.name) }),
                            onFocus: ($event: any) => (handleFocus(attack.name)),
                            onBlur: 
                    () => {
                      handleBlur();
                      checkValue(attack.name, armor.name);
                    }
                  ,
                            onKeydown: ($event: any) => (handleKeyNavigation($event, attack.name, armor.name))
                          }, null, 8, ["modelValue", "onUpdate:modelValue", "style", "onFocus", "onBlur", "onKeydown"])
                        ], 8, _hoisted_5)
                      ]))
                    }), 128))
                  ], 2))
                }), 128))
              ])
            ]),
            _: 1
          })
        ]),
        (hardcoreMode.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(timerDisplay.value), 1))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    (showConfetti.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createVNode(_unref(ConfettiExplosion), {
            particleCount: 300,
            particleSize: 12,
            duration: 15000
          })
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})